import { CSSProperties, ReactNode, useLayoutEffect } from "react";
import {
  ContextTabs,
  stickyRowClassName
} from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectLayout/ui/blocks/ContextTabs";
import { useStuck } from "@/zapi-frontend/shared/hooks/useStuck";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TSectionPage } from "../model/types";

// The tab row plus the 8px band of page color that keeps pinned card headers off the tabs.
const SECTION_TABS_HEIGHT = "3.25rem";

// The 40px title plus the same 8px band, until the row is measured.
const SECTION_TITLE_ROW_HEIGHT = "var(--section-title-row-height, 3rem)";

export const SECTION_STICKY_GAP = "1rem";

export const sectionStickyTop = (hasTabs: boolean) =>
  `calc(var(--app-header-height) + ${hasTabs ? SECTION_TABS_HEIGHT : SECTION_STICKY_GAP})`;

export const SECTION_TITLE_STICKY_TOP = `calc(var(--app-header-height) + ${SECTION_TITLE_ROW_HEIGHT})`;

const TITLE_CLASS = "typo-h3 min-h-10 shrink-0 text-foreground";

function StickyTitleRow({ title, actions }: { title: string; actions: ReactNode }) {
  const { ref, stuck } = useStuck<HTMLDivElement>();

  useLayoutEffect(() => {
    const row = ref.current;
    const section = row?.parentElement;
    if (!row || !section) return;
    const observer = new ResizeObserver(() =>
      section.style.setProperty("--section-title-row-height", `${row.offsetHeight}px`)
    );
    observer.observe(row);
    return () => observer.disconnect();
  }, [ref]);

  return (
    <div
      ref={ref}
      data-slot="section-title-row"
      data-stuck={stuck || undefined}
      className={cn(stickyRowClassName, "mb-4 flex min-w-0 items-center gap-2")}
    >
      <h1 className={cn(TITLE_CLASS, "min-w-0 flex-1")}>{title}</h1>
      <div data-slot="section-title-actions" className="flex shrink-0 items-center gap-1">
        {actions}
      </div>
    </div>
  );
}

type TSectionPageProps = {
  page: TSectionPage;
  fill?: boolean;
  actions?: ReactNode;
  children: ReactNode;
};

export function SectionPage({ page, fill, actions, children }: TSectionPageProps) {
  const hasTabs = page.tabs.length > 0;
  return (
    <div
      data-testid="section-page"
      style={hasTabs ? undefined : ({ "--widget-sticky-gap": SECTION_STICKY_GAP } as CSSProperties)}
      className="w-full min-w-0"
    >
      <section
        className={cn(
          "mx-auto flex w-full max-w-[960px] flex-col",
          fill ? "h-[calc(100svh-var(--app-header-height)-1rem)] min-h-0 pb-3" : "pb-8"
        )}
      >
        {!hasTabs && actions ? (
          <StickyTitleRow title={page.title} actions={actions} />
        ) : (
          <h1 className={cn(TITLE_CLASS, hasTabs ? "mb-2" : "mb-6")}>{page.title}</h1>
        )}
        {hasTabs && (
          <ContextTabs
            tabs={page.tabs}
            value={page.activeTabId}
            onValueChange={page.onTabChange}
            sticky
            actions={actions}
            className="shrink-0"
          />
        )}
        <div className={cn("flex min-w-0 flex-col", hasTabs && "pt-6", fill && "min-h-0 flex-1")}>{children}</div>
      </section>
    </div>
  );
}
